import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import { useDispatch, useSelector } from 'react-redux';
import CompletionMessage from 'components/common/CompletionMessage';
import HeaderLogo from 'components/common/headerLogo';
import Spinner from 'components/spinner/Spinner';
import useCommuniqueState from 'hooks/useCommuniqueState';
import useIncompleteRtmRedirect from 'hooks/useIncompleteRtmRedirect';
import { useGetPCCItemsByPatientId, useGetRTMEnrollmentInfo } from 'hooks/useApi';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { RootState } from 'store/store';
import { ModuleTypes } from 'types/appState';
import { isEmptyString } from 'utils/helper';
import RTMEnrollmentFlow from 'components/rtmEnrollment/RTMEnrollmentFlow';
import { getRtmEnrollmentStyles } from 'components/rtmEnrollment/rtmEnrollmentStyles';
import { isTabletBaseIntake, patientPortalLoginUrl } from 'env';

function setViewportHeight() {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

type StatusMessageProps = {
  title: string;
  message: string;
  isError?: boolean;
  onBack: () => void;
};

function StatusMessage({
  title,
  message,
  isError = false,
  onBack,
}: StatusMessageProps) {
  const styles = getRtmEnrollmentStyles();
  const { appointmentToken } = useSelector((state: RootState) => state.appStateSettings);
  const isTablet = isTabletBaseIntake || !isNil(appointmentToken);

  return (
    <div className={styles.layout.statusMessage}>
      <h2 className={clsx(styles.layout.statusMessageTitle, isError ? 'text-red-600' : 'text-gray-900')}>
        {title}
      </h2>
      <p className={styles.layout.statusMessageBody}>{message}</p>
      {isTablet && (
        <button
          type="button"
          className={styles.layout.statusMessageButton}
          onClick={onBack}
        >
          Back to choices
        </button>
      )}
    </div>
  );
}

type RtmEnrollmentProps = {
  onDisplayChooseScreen?: () => void;
  tokenOverride?: string;
  onEnrollmentComplete?: () => void;
};

function RtmEnrollment({
  onDisplayChooseScreen,
  tokenOverride,
  onEnrollmentComplete,
}: RtmEnrollmentProps) {
  const dispatch = useDispatch();
  const styles = getRtmEnrollmentStyles();
  const { rtmToken, appointmentToken } = useSelector((state: RootState) => state.appStateSettings);
  const isTablet = isTabletBaseIntake || !isNil(appointmentToken);
  const token = tokenOverride ?? rtmToken ?? '';
  const { data, isLoading, isError } = useGetRTMEnrollmentInfo(token, !!token);
  const { mutateAsync: getPCCItems } = useGetPCCItemsByPatientId();
  const { goToCommuniqueChoice } = useIncompleteRtmRedirect();
  const { communiqueData, setCommuniqueData } = useCommuniqueState();
  const [showSubmittedMessage, setShowSubmittedMessage] = useState(false);

  const showCompletedMessage = Boolean(data?.isCompleted) || showSubmittedMessage;
  const showExpiredMessage = !showCompletedMessage && Boolean(data?.isExpired);
  const showFlow = !showCompletedMessage
    && !showExpiredMessage
    && data?.canCompleteEnrollment === true;
  const showBlockedMessage = !showCompletedMessage
    && !showExpiredMessage
    && data?.canCompleteEnrollment === false;
  const hasTokenError = token.length === 0;

  useEffect(() => {
    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);

    return () => {
      window.removeEventListener('resize', setViewportHeight);
    };
  }, []);

  const handleEnrollmentCompleted = useCallback(async () => {
    if (!isNil(communiqueData)) {
      setCommuniqueData({
        ...communiqueData,
        rtm: {
          ...communiqueData.rtm,
          hasCompleted: true,
          kind: 'enrollment',
        },
      });
    }

    if (onEnrollmentComplete) {
      onEnrollmentComplete();
      return;
    }

    const patientId = communiqueData?.patientId;
    if (patientId) {
      try {
        const response = await getPCCItems(patientId);
        const rtmItem = response?.rtm;
        const checkInToken = rtmItem?.token;
        const isCheckInAvailable = Boolean(
          rtmItem?.isEnabled
          && !isEmptyString(checkInToken)
          && rtmItem?.status !== 'Completed',
        );

        if (isCheckInAvailable && checkInToken) {
          dispatch(
            appStateSettingsAction({
              name: ModuleTypes.rtmCheckIn,
              page: null,
              rtmToken: checkInToken,
              rtmKind: 'checkIn',
            }),
          );
          return;
        }
      } catch (error) {
        console.error('Error fetching PCC items on enrollment complete:', error);
      }
    }

    if (onDisplayChooseScreen) {
      onDisplayChooseScreen();
    } else if (isTablet) {
      goToCommuniqueChoice();
    } else {
      setShowSubmittedMessage(true);
    }
  }, [
    communiqueData,
    dispatch,
    getPCCItems,
    goToCommuniqueChoice,
    onDisplayChooseScreen,
    onEnrollmentComplete,
    setCommuniqueData,
  ]);

  return (
    <div
      className={styles.layout.page}
      style={{
        backgroundImage: 'url(/background-shadow.webp)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom',
        backgroundSize: 'cover',
        WebkitOverflowScrolling: 'touch',
        height: 'calc(var(--vh, 1vh) * 100)',
      }}
    >
      <div className={styles.layout.header}>
        <HeaderLogo />
      </div>

      <div className={styles.layout.contentArea}>
        {isLoading ? (
          <div className={styles.layout.loadingContainer}>
            <Spinner />
          </div>
        ) : (
          <>
            {(isError || hasTokenError) && (
              <div className={styles.layout.errorContainer}>
                {isError && (
                  <p className={styles.layout.errorText}>
                    Unable to load enrollment details. Please try again later.
                  </p>
                )}
                {hasTokenError && (
                  <p className={styles.layout.errorText}>
                    A valid enrollment link is required to continue.
                  </p>
                )}
                {isTablet && (
                  <button
                    type="button"
                    className={styles.layout.errorButton}
                    onClick={goToCommuniqueChoice}
                  >
                    Back to choices
                  </button>
                )}
              </div>
            )}

            {showCompletedMessage && (
              <CompletionMessage
                title="Enrollment Complete"
                message="You're enrolled in Treatment Progress Monitoring. Your care team will reach out with your first check-in."
                actionLabel="Go to patient portal"
                actionHref={patientPortalLoginUrl}
              />
            )}

            {showExpiredMessage && (
              <StatusMessage
                isError
                title="Enrollment Expired"
                message="This enrollment link has expired. Please contact your care team for a new link."
                onBack={goToCommuniqueChoice}
              />
            )}

            {showFlow && (
              <div className={styles.layout.flowContainer}>
                <RTMEnrollmentFlow
                  token={token}
                  patientName={data?.patientName}
                  providerName={data?.orderingProvider?.name}
                  organizationName={data?.organizationName}
                  onCompleted={handleEnrollmentCompleted}
                />
              </div>
            )}

            {showBlockedMessage && (
              <StatusMessage
                isError
                title="Enrollment Unavailable"
                message="This enrollment link is not currently available. Please contact your care team."
                onBack={goToCommuniqueChoice}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default RtmEnrollment;
